/**
 * Quote the whole field in one pass.
 *
 * Posting twelve markets one at a time is a form-filling exercise, and the lines
 * move every evening once the standings do — so the bookie would be doing it
 * again tomorrow. This is the roster with a price next to each name: type over
 * the numbers that changed, post once.
 *
 * Only the changed rows are written. Re-posting an unchanged line would churn
 * the sync cursor for every phone on the property and, worse, look to a player
 * like the price had moved when it had not.
 */

import { useMemo, useState } from 'react'
import type { Tournament } from '../engine/types'
import { impliedProbability, isValidAmericanOdds } from '../engine/odds'
import { postOdds, updateOdds, type Book, type OddsMarket } from '../store'
import { SectionRule } from '../ui'

/** The market a per-player line belongs to. One row per player, per book. */
const OUTRIGHT = 'outright'

export function QuoteField({
  doc,
  book,
  onDone,
}: {
  doc: Tournament
  book: Book
  onDone: () => void
}) {
  const players = doc.players.filter((p) => p.active)

  /** The open outright line for each player, if the book already has one. */
  const existing = useMemo(() => {
    const m = new Map<string, OddsMarket>()
    for (const o of book.odds) {
      if (o.market !== OUTRIGHT || !o.subject_id) continue
      if (o.status === 'settled') continue
      m.set(o.subject_id, o)
    }
    return m
  }, [book])

  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      players.map((p) => [p.id, existing.get(p.id)?.american_odds?.toString() ?? '']),
    ),
  )
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const changed = players.filter((p) => {
    const typed = (draft[p.id] ?? '').trim()
    const now = existing.get(p.id)?.american_odds
    if (typed === '') return false
    return Number(typed) !== now
  })

  const post = async () => {
    setBusy(true)
    setNote(null)
    try {
      let posted = 0
      let moved = 0
      for (const p of changed) {
        const odds = Number((draft[p.id] ?? '').trim())
        if (!isValidAmericanOdds(odds)) continue
        const open = existing.get(p.id)
        if (open) {
          await updateOdds(open.id, { americanOdds: odds })
          moved += 1
        } else {
          await postOdds({
            market: OUTRIGHT,
            subjectId: p.id,
            description: `${p.displayName} to win the Woodsmen`,
            americanOdds: odds,
          })
          posted += 1
        }
      }
      setNote(
        posted + moved === 0
          ? 'Nothing changed.'
          : `${posted ? `${posted} posted` : ''}${posted && moved ? ', ' : ''}${
              moved ? `${moved} moved` : ''
            }.`,
      )
      onDone()
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not post the board.')
    } finally {
      setBusy(false)
      setTimeout(() => setNote(null), 4000)
    }
  }

  return (
    <>
      <SectionRule>Quote the field</SectionRule>
      <p className="px-4 pt-1 pb-2 text-[13px] text-mute leading-snug">
        One line per man to win the whole thing. Leave a box empty to skip him. Only
        what you change gets posted, so re-quoting tomorrow is a matter of typing over
        the numbers that moved.
      </p>

      <ul className="divide-y divide-rule">
        {players.map((p) => {
          const typed = (draft[p.id] ?? '').trim()
          const open = existing.get(p.id)
          const n = Number(typed)
          const valid = typed !== '' && isValidAmericanOdds(n)
          const isChanged = valid && n !== open?.american_odds
          return (
            <li key={p.id} className="px-4 py-2 flex items-center gap-3">
              <span className="bc-name text-sm flex-1 min-w-0 truncate">
                {p.displayName}
              </span>
              {valid && (
                <span className="text-[12px] text-mute tabular-nums">
                  {Math.round(impliedProbability(n) * 100)}%
                </span>
              )}
              <input
                inputMode="numeric"
                value={draft[p.id] ?? ''}
                onChange={(e) => setDraft({ ...draft, [p.id]: e.target.value })}
                placeholder={open ? String(open.american_odds) : '—'}
                aria-label={`${p.displayName} line`}
                className={`w-20 text-center border-2 px-2 py-2 bc-num text-sm bc-tap bg-paper ${
                  isChanged ? 'border-signal' : 'border-rule'
                }`}
              />
            </li>
          )
        })}
      </ul>

      {note && <p className="px-4 py-2 bc-label text-mute">{note}</p>}

      <div className="px-4 py-3">
        <button
          type="button"
          onClick={post}
          disabled={busy || changed.length === 0}
          className="bc-tap w-full bg-signal text-paper bc-label py-4 disabled:opacity-30"
        >
          {busy
            ? 'Posting…'
            : changed.length === 0
              ? 'Nothing to post'
              : `Post ${changed.length} line${changed.length === 1 ? '' : 's'}`}
        </button>
        <p className="text-[13px] text-mute leading-snug mt-2">
          A line already taken keeps the price it was taken at — moving it here never
          rewrites a bet somebody already placed.
        </p>
      </div>
    </>
  )
}
